import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Save, Send, ThumbsUp, TrendingUp } from 'lucide-react';
import { AssignmentStatus } from '@tutor/shared';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  Textarea,
} from '@tutor/ui';
import { ApiError } from '../../lib/api-client';
import {
  useHomeworkAssignment,
  useMarkHomework,
  useSaveHomeworkDraft,
  useSubmitHomework,
} from '../../hooks/queries';
import { formatDateTime, formatDue, formatScore } from '../../lib/format';

export function HomeworkDetailPage({ perspective }: { perspective: 'tutor' | 'student' }) {
  const { homeworkId } = useParams<{ homeworkId: string }>();
  const { data: assignment, isLoading, error } = useHomeworkAssignment(homeworkId);
  const basePath = perspective === 'tutor' ? '/tutor' : '/student';

  if (isLoading) return <LoadingState />;

  if (error) {
    const apiError = error instanceof ApiError ? error : null;
    return (
      <ErrorState
        title={apiError?.isForbidden ? 'You do not have access to this homework' : 'Homework not found'}
        description={apiError?.message}
        action={
          <Link to={`${basePath}/homework`}>
            <Button variant="outline">Back to homework</Button>
          </Link>
        }
      />
    );
  }

  if (!assignment) return null;

  const due = formatDue(assignment.dueAt);

  return (
    <>
      <Link
        to={`${basePath}/homework`}
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        All homework
      </Link>

      <PageHeader
        title={assignment.homeworkTitle}
        description={
          <span className="flex flex-wrap items-center gap-2">
            {assignment.subject && <Badge tone="brand">{assignment.subject.name}</Badge>}
            <span className={due.tone === 'danger' ? 'font-medium text-rose-600' : undefined}>{due.label}</span>
            <span aria-hidden>·</span>
            <span>
              {perspective === 'tutor'
                ? `${assignment.student.fullName}`
                : `Set by ${assignment.assignedBy.fullName}`}
            </span>
          </span>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader title="Instructions" />
            <CardBody>
              {assignment.description && <p className="text-sm text-slate-700">{assignment.description}</p>}
              {assignment.instructions ? (
                <p className="mt-3 whitespace-pre-wrap text-sm text-slate-700">{assignment.instructions}</p>
              ) : (
                !assignment.description && <p className="text-sm text-slate-500">No instructions were provided.</p>
              )}
            </CardBody>
          </Card>

          {perspective === 'student' ? (
            <StudentWorkPanel assignment={assignment} />
          ) : (
            <TutorMarkingPanel assignment={assignment} />
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Details" />
            <CardBody className="space-y-2 text-sm">
              <Row label="Status" value={<StatusBadge status={assignment.status} />} />
              <Row label="Due" value={formatDateTime(assignment.dueAt)} />
              <Row label="Set" value={formatDateTime(assignment.assignedAt)} />
              {assignment.submittedAt && <Row label="Submitted" value={formatDateTime(assignment.submittedAt)} />}
              {assignment.markedAt && <Row label="Marked" value={formatDateTime(assignment.markedAt)} />}
              {assignment.maxScore !== null && (
                <Row label="Out of" value={String(assignment.maxScore)} />
              )}
            </CardBody>
          </Card>

          {assignment.feedback && (
            <Card>
              <CardHeader title="Feedback" />
              <CardBody className="space-y-3">
                <div className="flex items-center gap-2">
                  <Avatar name={assignment.feedback.markedBy.fullName} src={assignment.feedback.markedBy.avatarUrl} size="sm" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">
                      {assignment.feedback.markedBy.fullName}
                    </p>
                    <p className="text-xs text-slate-500">{formatDateTime(assignment.feedback.markedAt)}</p>
                  </div>
                  {assignment.feedback.score !== null && (
                    <Badge tone="success" className="ml-auto">
                      {formatScore(assignment.feedback.score, assignment.feedback.maxScore)}
                    </Badge>
                  )}
                </div>

                {assignment.feedback.comment && (
                  <p className="whitespace-pre-wrap text-sm text-slate-700">{assignment.feedback.comment}</p>
                )}

                {assignment.feedback.strengths.length > 0 && (
                  <div>
                    <p className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-emerald-600">
                      <ThumbsUp className="h-3.5 w-3.5" aria-hidden />
                      Strengths
                    </p>
                    <ul className="space-y-1">
                      {assignment.feedback.strengths.map((item) => (
                        <li key={item} className="text-sm text-slate-700">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {assignment.feedback.improvements.length > 0 && (
                  <div>
                    <p className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-amber-600">
                      <TrendingUp className="h-3.5 w-3.5" aria-hidden />
                      To work on
                    </p>
                    <ul className="space-y-1">
                      {assignment.feedback.improvements.map((item) => (
                        <li key={item} className="text-sm text-slate-700">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900">{value}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: AssignmentStatus }) {
  const map = {
    ASSIGNED: { tone: 'neutral', label: 'Not started' },
    IN_PROGRESS: { tone: 'info', label: 'In progress' },
    SUBMITTED: { tone: 'warning', label: 'Submitted' },
    MARKED: { tone: 'success', label: 'Marked' },
    RETURNED: { tone: 'success', label: 'Returned' },
    OVERDUE: { tone: 'danger', label: 'Overdue' },
  } as const;

  const entry = map[status];
  return <Badge tone={entry.tone}>{entry.label}</Badge>;
}

/* ------------------------------- student -------------------------------- */

function StudentWorkPanel({
  assignment,
}: {
  assignment: NonNullable<ReturnType<typeof useHomeworkAssignment>['data']>;
}) {
  const [body, setBody] = useState(assignment.submission?.body ?? '');
  const [notice, setNotice] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const saveDraft = useSaveHomeworkDraft(assignment.id);
  const submit = useSubmitHomework(assignment.id);

  // Reset the editor when the server sends a newer version of the submission.
  useEffect(() => {
    setBody(assignment.submission?.body ?? '');
  }, [assignment.submission?.id, assignment.submission?.body]);

  const isLocked =
    assignment.status === AssignmentStatus.SUBMITTED || assignment.status === AssignmentStatus.MARKED;

  const run = async (action: 'save' | 'submit'): Promise<void> => {
    setNotice(null);
    setErrorMessage(null);
    try {
      if (action === 'save') {
        await saveDraft.mutateAsync({ body });
        setNotice('Draft saved.');
      } else {
        await submit.mutateAsync({ body });
        setNotice('Submitted. Your tutor has been notified.');
      }
    } catch (error) {
      setErrorMessage(error instanceof ApiError ? error.message : 'Something went wrong. Please try again.');
    }
  };

  return (
    <Card>
      <CardHeader
        title="Your work"
        description={isLocked ? 'This has been submitted and can no longer be edited.' : 'Show your working below.'}
      />
      <CardBody className="space-y-4">
        {notice && (
          <Alert tone="success">
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              {notice}
            </span>
          </Alert>
        )}
        {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}

        <Textarea
          rows={10}
          value={body}
          disabled={isLocked}
          onChange={(event) => setBody(event.target.value)}
          placeholder="Type your answers and working here…"
          aria-label="Your work"
        />

        {!isLocked && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => void run('save')}
              isLoading={saveDraft.isPending}
              leftIcon={<Save className="h-4 w-4" aria-hidden />}
            >
              Save draft
            </Button>
            <Button
              onClick={() => void run('submit')}
              isLoading={submit.isPending}
              disabled={!body.trim()}
              leftIcon={<Send className="h-4 w-4" aria-hidden />}
            >
              Submit homework
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/* -------------------------------- tutor --------------------------------- */

function TutorMarkingPanel({
  assignment,
}: {
  assignment: NonNullable<ReturnType<typeof useHomeworkAssignment>['data']>;
}) {
  const mark = useMarkHomework(assignment.id);
  const [score, setScore] = useState(assignment.feedback?.score?.toString() ?? '');
  const [grade, setGrade] = useState(assignment.feedback?.grade ?? '');
  const [comment, setComment] = useState(assignment.feedback?.comment ?? '');
  const [strengths, setStrengths] = useState(assignment.feedback?.strengths.join('\n') ?? '');
  const [improvements, setImprovements] = useState(assignment.feedback?.improvements.join('\n') ?? '');
  const [notice, setNotice] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const canMark =
    assignment.status === AssignmentStatus.SUBMITTED || assignment.status === AssignmentStatus.MARKED;

  const toList = (value: string): string[] =>
    value
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

  const onSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setNotice(null);
    setErrorMessage(null);

    try {
      await mark.mutateAsync({
        score: score === '' ? undefined : Number(score),
        grade: grade || undefined,
        comment: comment || undefined,
        strengths: toList(strengths),
        improvements: toList(improvements),
      });
      setNotice('Marked. The student has been notified.');
    } catch (error) {
      setErrorMessage(error instanceof ApiError ? error.message : 'We could not save your marking.');
    }
  };

  return (
    <>
      <Card>
        <CardHeader title="Student's submission" />
        <CardBody>
          {assignment.submission?.body ? (
            <p className="whitespace-pre-wrap text-sm text-slate-700">{assignment.submission.body}</p>
          ) : (
            <p className="text-sm text-slate-500">Nothing submitted yet.</p>
          )}
          {assignment.submission?.submittedAt && (
            <p className="mt-3 text-xs text-slate-400">
              Submitted {formatDateTime(assignment.submission.submittedAt)}
            </p>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Marking"
          description={canMark ? undefined : 'You can mark this once the student submits.'}
        />
        <CardBody>
          <form onSubmit={onSubmit} className="space-y-4">
            {notice && <Alert tone="success">{notice}</Alert>}
            {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label={`Score${assignment.maxScore !== null ? ` (out of ${assignment.maxScore})` : ''}`}
                type="number"
                min={0}
                max={assignment.maxScore ?? undefined}
                value={score}
                disabled={!canMark}
                onChange={(event) => setScore(event.target.value)}
              />
              <Input
                label="Grade"
                placeholder="A"
                value={grade}
                disabled={!canMark}
                onChange={(event) => setGrade(event.target.value)}
              />
            </div>

            <Textarea
              label="Comment"
              rows={4}
              value={comment}
              disabled={!canMark}
              onChange={(event) => setComment(event.target.value)}
              placeholder="What went well, and what to do next…"
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <Textarea
                label="Strengths"
                hint="One per line"
                rows={3}
                value={strengths}
                disabled={!canMark}
                onChange={(event) => setStrengths(event.target.value)}
              />
              <Textarea
                label="To work on"
                hint="One per line"
                rows={3}
                value={improvements}
                disabled={!canMark}
                onChange={(event) => setImprovements(event.target.value)}
              />
            </div>

            <Button type="submit" isLoading={mark.isPending} disabled={!canMark}>
              {assignment.status === AssignmentStatus.MARKED ? 'Update marking' : 'Save marking'}
            </Button>
          </form>
        </CardBody>
      </Card>
    </>
  );
}
